import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { parseUTC, formatDakar } from "@/lib/time";
import HistoryDetail from "@/components/HistoryDetail";
import {
  ShieldAlert, ArrowLeft, ClipboardList, User, UserCheck, MapPinned, Clock, Timer, Loader2, ChevronRight,
} from "lucide-react";

const fmtDuration = (fromIso, toIso) => {
  const a = parseUTC(fromIso), b = parseUTC(toIso);
  if (!a || !b) return "—";
  let s = Math.max(0, Math.floor((b.getTime() - a.getTime()) / 1000));
  const h = Math.floor(s / 3600); s %= 3600;
  const m = Math.floor(s / 60); const sec = s % 60;
  if (h) return `${h} h ${m} min`;
  if (m) return `${m} min ${sec}s`;
  return `${sec}s`;
};

const History = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const openDetail = (id) => { setSelectedId(id); setDetailOpen(true); };

  const load = () => {
    // Show every alert — the live/active ones AND the finished (resolved/cancelled)
    // history — deduped and sorted newest-first so recent alerts never go missing.
    Promise.all([
      api.history().catch(() => []),
      api.activeAlerts().then((d) => d.alerts || []).catch(() => []),
    ])
      .then(([hist, active]) => {
        const byId = new Map();
        (Array.isArray(active) ? active : []).forEach((a) => a?.alert_id && byId.set(a.alert_id, a));
        // History entries overwrite active ones so a finished alert shows its final status.
        (Array.isArray(hist) ? hist : []).forEach((a) => a?.alert_id && byId.set(a.alert_id, a));
        const merged = [...byId.values()].sort(
          (a, b) => (parseUTC(b.triggered_at)?.getTime() || 0) - (parseUTC(a.triggered_at)?.getTime() || 0)
        );
        setItems(merged);
      })
      .catch(() => toast.error("Impossible de charger l'historique"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  const statusMeta = (s) => {
    const v = (s || "").toLowerCase();
    if (v.startsWith("cancel")) return { label: "Annulé", cls: "bg-red-500/10 text-red-400 border-red-500/30" };
    if (v === "resolved") return { label: "Traitée", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" };
    if (v === "active" || v === "") return { label: "En cours", cls: "bg-amber-500/10 text-amber-400 border-amber-500/30 animate-pulse" };
    return { label: s || "Terminée", cls: "bg-slate-500/10 text-slate-300 border-slate-600" };
  };

  const filtered = items.filter((it) =>
    filter === "all" ? true : filter === "cancelled" ? it.status === "cancelled" : it.status === "resolved"
  );

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      <header className="h-16 sticky top-0 z-40 backdrop-blur-md bg-slate-900/80 border-b border-slate-800 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display font-bold leading-tight flex items-center gap-2"><ClipboardList className="w-4 h-4 text-emerald-400" /> Historique des interventions</h1>
            <p className="text-[11px] font-mono text-slate-500">Sama Jaamu — Alertes traitées &amp; annulées</p>
          </div>
        </div>
        <Button data-testid="history-back-btn" onClick={() => navigate("/")} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour au dashboard
        </Button>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="font-display font-semibold text-lg">Alertes actives &amp; terminées</h2>
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              {[
                { k: "all", label: "Toutes", tid: "history-filter-all" },
                { k: "resolved", label: "Traitées", tid: "history-filter-resolved" },
                { k: "cancelled", label: "Annulées", tid: "history-filter-cancelled" },
              ].map((f) => (
                <button key={f.k} data-testid={f.tid} onClick={() => setFilter(f.k)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-all ${filter === f.k ? "bg-emerald-600 border-emerald-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <span className="font-mono text-xs text-slate-500" data-testid="interventions-count">{filtered.length} alerte(s)</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-slate-900/60 border border-dashed border-slate-700 rounded-2xl p-10 text-center text-slate-500">
            <ClipboardList className="w-8 h-8 mx-auto mb-3 text-slate-600" />
            <p className="font-display font-semibold text-slate-300">Aucune alerte dans l'historique</p>
            <p className="text-sm mt-1">Les alertes traitées et annulées apparaîtront ici avec leur statut.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="interventions-list">
            {filtered.map((it) => {
              const meta = statusMeta(it.status);
              return (
              <div key={it.alert_id} data-testid={`intervention-row-${it.alert_id}`} onClick={() => openDetail(it.alert_id)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(it.alert_id); } }} className="bg-slate-900/80 border border-slate-800 rounded-xl p-5 cursor-pointer transition-all hover:border-emerald-500/50 hover:bg-slate-900 focus:outline-none focus:border-emerald-500">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <p className="font-display font-semibold text-slate-100 flex items-center gap-2">
                    <User className="w-4 h-4 text-emerald-400" /> {it.user_name || "Victime inconnue"}
                  </p>
                  <div className="flex items-center gap-2 shrink-0">
                    <span data-testid={`intervention-status-${it.alert_id}`} className={`text-[11px] font-mono border px-2.5 py-1 rounded-full ${meta.cls}`}>{meta.label}</span>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 pt-4 border-t border-slate-800/80 text-xs font-mono text-slate-400">
                  <div className="flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-yellow-400" />
                    {it.assigned_to ? <span className="text-slate-200">{it.assigned_to}</span> : <span className="text-slate-500">Aucun agent affecté</span>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Timer className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-slate-500">Durée :</span>
                    <span className="text-slate-200" data-testid={`intervention-duration-${it.alert_id}`}>
                      {fmtDuration(it.triggered_at, it.ended_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-slate-500">Déclenchée :</span> {it.triggered_at ? formatDakar(it.triggered_at) : "—"}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MapPinned className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-slate-500">{it.status === "cancelled" ? "Annulée :" : "Clôturée :"}</span> {it.ended_at ? formatDakar(it.ended_at) : "—"}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </main>

      <HistoryDetail alertId={selectedId} open={detailOpen} onClose={() => setDetailOpen(false)} />
    </div>
  );
};

export default History;
